import React from 'react';
import { FileCheck2 } from 'lucide-react';
import type { AaiRequest } from '../../../types/aai';

// Shared primitives for the role dashboards — a 1:1 translation of the imported
// design's building blocks into the app's Tailwind brand tokens.
//   .card  -> bg-brand-50            black hero -> bg-black + accent mono labels
//   mono labels: uppercase, tracked, brand-500 (or accent-500 on dark)

// bg-brand-50 (#EEF3FF) === the design's --aa-card cool off-white surface;
// rounded-aa (14px) is the design's canonical card radius.
export const Card: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <div className={`bg-card rounded-aa p-5 ${className}`}>{children}</div>
);

export const Hero: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <div className={`bg-black text-white rounded-aa p-5 ${className}`}>{children}</div>
);

export const MonoLabel: React.FC<{ tone?: 'brand' | 'accent' | 'gray'; className?: string; children: React.ReactNode }> = ({
  tone = 'brand', className = '', children,
}) => {
  const color = tone === 'accent' ? 'text-accent-500' : tone === 'gray' ? 'text-gray-400' : 'text-brand-600';
  return (
    <div className={`font-sans text-[11px] font-semibold uppercase tracking-[0.08em] ${color} ${className}`}>{children}</div>
  );
};

const BANNER_TONES: Record<string, string> = {
  blue: 'bg-brand-50 border-brand-200 text-brand-800',
  amber: 'bg-accent-100/60 border-accent-300 text-amber-800',
  green: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  purple: 'bg-purple-50 border-purple-200 text-purple-800',
};

export const Banner: React.FC<{
  tone?: 'blue' | 'amber' | 'green' | 'purple';
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ tone = 'blue', icon, children, className = '' }) => (
  <div className={`flex items-center gap-2.5 rounded-lg border px-3.5 py-2.5 mb-5 ${BANNER_TONES[tone]} ${className}`}>
    {icon}
    <span className="text-[13px] leading-snug">{children}</span>
  </div>
);

export const StatCard: React.FC<{ value: React.ReactNode; label: string; valueClass?: string }> = ({ value, label, valueClass = '' }) => (
  <Card className="!p-4">
    <div className={`text-2xl font-bold tracking-tight ${valueClass}`}>{value}</div>
    <div className="text-xs text-gray-500 mt-0.5">{label}</div>
  </Card>
);

const BAR_COLORS: Record<string, string> = {
  brand: 'bg-brand-500', green: 'bg-emerald-500', amber: 'bg-amber-400', gray: 'bg-gray-300',
};

export const Bar: React.FC<{ pct: string; color?: keyof typeof BAR_COLORS; track?: string; height?: string }> = ({
  pct, color = 'brand', track = 'bg-white', height = 'h-2',
}) => (
  <div className={`${height} w-full rounded-full ${track}`}>
    <div className={`${height} rounded-full ${BAR_COLORS[color]}`} style={{ width: pct }} />
  </div>
);

const STAGE_PILL: Record<string, string> = {
  nascent: 'bg-gray-100 text-gray-700',
  early: 'bg-amber-50 text-amber-700',
  developing: 'bg-brand-50 text-brand-700',
  mature: 'bg-indigo-50 text-indigo-700',
  advanced: 'bg-emerald-50 text-emerald-700',
};

export const StageBand: React.FC<{ stage?: string | null; className?: string }> = ({ stage, className = '' }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${STAGE_PILL[stage || ''] || 'bg-gray-100 text-gray-500'} ${className}`}>
    {stage ? stage.charAt(0).toUpperCase() + stage.slice(1) : '—'}
  </span>
);

const RISK_PILL: Record<string, string> = {
  low: 'bg-emerald-50 text-emerald-700',
  medium: 'bg-amber-50 text-amber-700',
  high: 'bg-red-50 text-red-600',
};

export const RiskPill: React.FC<{ risk: string }> = ({ risk }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${RISK_PILL[risk] || 'bg-gray-100 text-gray-600'}`}>
    {risk}
  </span>
);

export const Avatar: React.FC<{ initials: string; tone?: 'brand' | 'accent' }> = ({ initials, tone = 'brand' }) => (
  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${
    tone === 'accent' ? 'bg-accent-500 text-black' : 'bg-brand-50 text-brand-600'
  }`}>{initials}</div>
);

export const timeRange = (t: { low: number; high: number; unit: string }) =>
  `${t.low}–${t.high} ${t.unit}`;

// Two-letter initials for a client/workspace chip (the "PROGRAM FOR" bar).
export const initialsOf = (name: string): string => {
  const words = name.replace(/[/·|]/g, ' ').split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return (words[0] || '?').slice(0, 2).toUpperCase();
};

// Shared approval-queue card used by the dept-head (gate 1), champion and owner
// (gate 2 + deptless gate 1). "Ask more" only appears at gate 1 (status still
// pending/needs_info) — at the champion stage the request is dept_approved and
// only Approve / Decline apply.
export const ApprovalQueue: React.FC<{
  items: AaiRequest[];
  onDecide: (id: string, decision: string, comment?: string) => void;
  busyId: string | null;
  title?: string;
}> = ({ items, onDecide, busyId, title = 'Approval queue' }) => {
  // Decline / ask-more open an inline reason box. A decline REQUIRES a reason —
  // it's relayed to the requester (PRD F3: "notifies the requester with the
  // reason"). "Ask more" reason is optional (the prompt itself is the ask).
  const [reasonFor, setReasonFor] = React.useState<{ id: string; decision: string } | null>(null);
  const [reason, setReason] = React.useState('');

  const openReason = (id: string, decision: string) => { setReason(''); setReasonFor({ id, decision }); };
  const submitReason = () => {
    if (!reasonFor) return;
    const text = reason.trim();
    if (reasonFor.decision === 'declined' && !text) return; // required
    onDecide(reasonFor.id, reasonFor.decision, text || undefined);
    setReasonFor(null); setReason('');
  };

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="font-bold text-sm">{title}</span>
        {items.length > 0 && <span className="text-[11px] font-bold bg-red-500 text-white px-2 py-0.5 rounded-full">{items.length} to review</span>}
      </div>
      {items.length === 0 && <p className="text-[13px] text-gray-400">Nothing awaiting your approval.</p>}
      {items.map((a) => {
        const canAskMore = a.status === 'pending' || a.status === 'needs_info';
        const open = reasonFor?.id === a.id;
        const declining = open && reasonFor?.decision === 'declined';
        return (
          <div key={a.id} className="border border-gray-100 rounded-lg p-3 mb-2.5 last:mb-0">
            <div className="flex justify-between items-start mb-1">
              <span className="font-semibold text-[13px]">{a.title}</span>
              <RiskPill risk={a.risk} />
            </div>
            <div className="text-[11.5px] text-gray-400 mb-2.5">
              from {a.requester_email}{a.department ? ` · ${a.department}` : ' · no department'}
              {a.status === 'dept_approved' && ' · approved by dept head'}
            </div>
            {open ? (
              <div>
                <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">
                  {declining ? 'Reason for declining (required)' : 'What more do you need? (optional)'}
                </label>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} autoFocus
                  placeholder={declining ? 'Tell the requester why this is being declined…' : 'Ask the requester for more detail…'}
                  className="w-full rounded-lg border border-gray-200 p-2.5 text-[12.5px] mb-2 focus:outline-none focus:ring-2 focus:ring-brand-200" />
                <div className="flex gap-2">
                  <button disabled={busyId === a.id || (declining && !reason.trim())} onClick={submitReason}
                    className={`text-[11.5px] font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-50 ${declining ? 'bg-red-600 hover:bg-red-700' : 'bg-brand-500 hover:bg-brand-600'}`}>
                    {declining ? 'Decline' : 'Send'}
                  </button>
                  <button disabled={busyId === a.id} onClick={() => { setReasonFor(null); setReason(''); }}
                    className="text-[11.5px] font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg disabled:opacity-50">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <button disabled={busyId === a.id} onClick={() => onDecide(a.id, 'approved')}
                  className="text-[11.5px] font-semibold bg-brand-500 hover:bg-brand-600 text-white px-3 py-1.5 rounded-lg disabled:opacity-50">Approve</button>
                {canAskMore && (
                  <button disabled={busyId === a.id} onClick={() => openReason(a.id, 'needs_info')}
                    className="text-[11.5px] font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg disabled:opacity-50">Ask more</button>
                )}
                <button disabled={busyId === a.id} onClick={() => openReason(a.id, 'declined')}
                  className="text-[11.5px] font-semibold text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg disabled:opacity-50">Decline</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── PageHeader — the design's kicker + .scr-h title + .scr-sub subtitle ───────
export const PageHeader: React.FC<{
  kicker?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}> = ({ kicker, title, subtitle, icon, action, className = '' }) => (
  <div className={`flex items-start justify-between gap-4 mb-5 ${className}`}>
    <div className="min-w-0">
      {kicker && <MonoLabel className="mb-1.5">{kicker}</MonoLabel>}
      <div className="flex items-center gap-2">
        {icon}
        <h1 className="text-[21px] font-bold tracking-[-0.01em] text-gray-900">{title}</h1>
      </div>
      {subtitle && <p className="text-[13.5px] text-gray-500 mt-1 max-w-2xl leading-snug">{subtitle}</p>}
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
);

// ── BandPill — department maturity bands (R3 aggregate framing) ───────────────
const BAND_PILL: Record<string, string> = {
  mature: 'bg-emerald-50 text-emerald-700',
  developing: 'bg-brand-50 text-brand-700',
  emerging: 'bg-amber-50 text-amber-700',
  suppressed: 'bg-gray-100 text-gray-500',
  hidden: 'bg-gray-100 text-gray-500',
};
export const BandPill: React.FC<{ band?: string | null; label?: string }> = ({ band, label }) => {
  const key = (band || '').toLowerCase();
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap ${BAND_PILL[key] || 'bg-gray-100 text-gray-600'}`}>
      {label || (band ? band.charAt(0).toUpperCase() + band.slice(1) : '—')}
    </span>
  );
};

// ── StatusPill — generic status chip used across requests / builds / registry ─
const STATUS_PILL: Record<string, string> = {
  approved: 'bg-emerald-50 text-emerald-700',
  live: 'bg-emerald-50 text-emerald-700',
  shipped: 'bg-emerald-50 text-emerald-700',
  complete: 'bg-emerald-50 text-emerald-700',
  active: 'bg-emerald-50 text-emerald-700',
  in_review: 'bg-amber-50 text-amber-700',
  'in review': 'bg-amber-50 text-amber-700',
  testing: 'bg-amber-50 text-amber-700',
  scoped: 'bg-amber-50 text-amber-700',
  pending: 'bg-amber-50 text-amber-700',
  dept_approved: 'bg-brand-50 text-brand-700',
  in_build: 'bg-brand-50 text-brand-700',
  'in build': 'bg-brand-50 text-brand-700',
  scheduled: 'bg-brand-50 text-brand-700',
  needs_info: 'bg-amber-50 text-amber-700',
  blocked: 'bg-red-50 text-red-600',
  declined: 'bg-gray-100 text-gray-500',
  backlog: 'bg-gray-100 text-gray-500',
  locked: 'bg-gray-100 text-gray-500',
};
export const StatusPill: React.FC<{ status?: string | null; label?: string; className?: string }> = ({ status, label, className = '' }) => {
  const key = (status || '').toLowerCase();
  const text = label ?? (status ? status.replace(/_/g, ' ') : '—');
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize whitespace-nowrap ${STATUS_PILL[key] || 'bg-gray-100 text-gray-600'} ${className}`}>
      {text}
    </span>
  );
};

// ── AuditStrip — black "opening an individual is logged" governance strip (R1) ─
export const AuditStrip: React.FC<{
  title: React.ReactNode;
  children: React.ReactNode;
  action?: React.ReactNode;
  icon?: React.ReactNode;
}> = ({ title, children, action, icon }) => (
  <div className="bg-black text-white rounded-xl p-5 flex items-center gap-4">
    <span className="w-10 h-10 shrink-0 rounded-xl bg-white/10 text-white grid place-items-center">
      {icon || <FileCheck2 size={20} />}
    </span>
    <div className="flex-1 min-w-0">
      <div className="font-bold text-sm mb-0.5">{title}</div>
      <p className="text-[12.5px] text-white/60 leading-relaxed m-0">{children}</p>
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
);

// ── Toggle — the design's 38×22 pill switch ──────────────────────────────────
export const Toggle: React.FC<{ on: boolean; onChange?: (next: boolean) => void; disabled?: boolean }> = ({ on, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    disabled={disabled}
    onClick={() => onChange?.(!on)}
    className={`relative w-[38px] h-[22px] rounded-full transition-colors disabled:opacity-60 ${on ? 'bg-brand-500' : 'bg-gray-300'}`}
  >
    <span className={`absolute top-0.5 w-[18px] h-[18px] rounded-full bg-white transition-all ${on ? 'right-0.5' : 'left-0.5'}`} />
  </button>
);

// ── SegChips — segmented chip selector (e.g. request kind) ────────────────────
export const SegChips: React.FC<{
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  className?: string;
}> = ({ value, options, onChange, className = '' }) => (
  <div className={`inline-flex gap-2 flex-wrap ${className}`}>
    {options.map((o) => {
      const on = o.value === value;
      return (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`text-[12.5px] font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${
            on ? 'border-brand-500 bg-brand-500 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-brand-300'
          }`}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

// ── StageTrack — the readiness bar over the doc's 5 stages ────────────────────
// Matches the scoring scale exactly (orbit_scoring.STAGES): nascent → early →
// developing → mature → advanced. Bars fill up to the current stage; the current
// label is bolded.
const STAGE5: { key: string; label: string }[] = [
  { key: 'nascent', label: 'Nascent' },
  { key: 'early', label: 'Early' },
  { key: 'developing', label: 'Developing' },
  { key: 'mature', label: 'Mature' },
  { key: 'advanced', label: 'Advanced' },
];
const stage5Index = (stage?: string | null) => STAGE5.findIndex((s) => s.key === (stage || '').toLowerCase());

export const StageTrack: React.FC<{ stage?: string | null; dark?: boolean }> = ({ stage, dark = false }) => {
  const cur = stage5Index(stage);
  return (
    <div className="flex gap-1.5 items-end">
      {STAGE5.map((s, i) => {
        const filled = cur >= 0 && i <= cur;
        const isCurrent = i === cur;
        const bar = filled ? 'bg-emerald-400' : dark ? 'bg-white/20' : 'bg-gray-200';
        const lbl = isCurrent
          ? (dark ? 'text-white font-semibold' : 'text-gray-900 font-semibold')
          : (dark ? 'text-white/45' : 'text-gray-400');
        return (
          <div key={s.key} className="flex-1 text-center">
            <div className={`h-1.5 rounded-full mb-1.5 ${bar}`} />
            <div className={`text-[10.5px] ${lbl}`}>{s.label}</div>
          </div>
        );
      })}
    </div>
  );
};

// ── VBars — small vertical bar chart (e.g. "time redirected per week") ─────────
export const VBars: React.FC<{ bars: { label: string; pct: number }[]; height?: number }> = ({ bars, height = 120 }) => (
  <div className="flex items-end gap-2" style={{ height }}>
    {bars.map((b, i) => {
      // later bars trend brand-strong; earlier bars are faded — pure presentation
      const shade = i >= bars.length - 2 ? 'bg-brand-500' : i >= bars.length - 4 ? 'bg-brand-300' : 'bg-brand-100';
      return (
        <div key={`${b.label}-${i}`} className="flex-1 flex flex-col items-center justify-end gap-1.5 h-full">
          <div className={`w-full rounded-t-md ${shade}`} style={{ height: `${Math.max(2, Math.min(100, b.pct))}%` }} />
          <span className="font-sans text-[10px] text-gray-400">{b.label}</span>
        </div>
      );
    })}
  </div>
);
